import jsPDF from 'jspdf';
import { drawArucoMarker, MARKER_IDS, FORM_LAYOUT } from './arucoMarkers';

interface Question {
  question_number: number;
  number_of_answers: number;
  part_id: number | null;
}

interface Part {
  id: number;
  titre: string;
  ordre: number;
}

interface Test {
  id: number;
  title: string;
  total_questions: number;
  questions: Question[];
  parts: Part[];
}

export const generateBubbleForm = async (test: Test): Promise<void> => {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = FORM_LAYOUT.margin;
  const contentWidth = pageWidth - 2 * margin;
  const bottomMarkerInset = FORM_LAYOUT.bottomMarkerInset;
  const markerInsetTop = FORM_LAYOUT.markerInset;
  const markerSize = FORM_LAYOUT.markerSize;
  const markerInset = FORM_LAYOUT.markerInset;
  const contentHeight = pageHeight - markerInsetTop - bottomMarkerInset - markerSize - 2;

  // Configuration des bulles
  const bubbleRadius = FORM_LAYOUT.bubbleRadius;
  const bubbleSpacing = FORM_LAYOUT.bubbleSpacing;
  const questionHeight = FORM_LAYOUT.questionHeight;

  // Grouper les questions par partie
  const questionsByPart: { [key: string]: Question[] } = {};
  const partsMap: { [key: number]: Part } = {};

  // Créer un map des parts
  test.parts.forEach(part => {
    partsMap[part.id] = part;
    questionsByPart[`part_${part.id}`] = [];
  });

  // Les questions sans partie
  questionsByPart['no_part'] = [];

  // Trier les questions par partie
  test.questions.forEach(question => {
    if (question.part_id && partsMap[question.part_id]) {
      questionsByPart[`part_${question.part_id}`].push(question);
    } else {
      questionsByPart['no_part'].push(question);
    }
  });

  // Fonction pour dessiner une question dans un cadre
  const drawQuestion = (question: Question, x: number, y: number, width: number) => {
    const questionNumWidth = FORM_LAYOUT.questionNumWidth;

    // Calculer le centre vertical de la ligne de question
    const verticalCenter = y + questionHeight / 2;

    const fontSize = 8;

    // Calculer le centre vertical de la bulle
    const bubbleY = verticalCenter;

    // Numéro de question avec trait plus épais - centré verticalement
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', 'bold');

    // Calculer la hauteur du texte du numéro de question
    const questionNumDimensions = doc.getTextDimensions(`${question.question_number}`);
    const fontHeight = questionNumDimensions.h;

    // Positionner le numéro de question avec le même centrage que les lettres
    doc.text(`${question.question_number}`, x + questionNumWidth / 2, bubbleY + fontHeight / 3, { align: 'center' });

    // Dessiner les bulles pour chaque réponse
    const bubbleStartX = x + questionNumWidth;

    for (let i = 0; i < question.number_of_answers; i++) {
      const bubbleX = bubbleStartX + i * bubbleSpacing + bubbleRadius;

      // Dessiner le cercle (bulle) avec trait plus épais
      doc.setLineWidth(0.4);
      doc.circle(bubbleX, bubbleY, bubbleRadius);

      // Lettre de la réponse À L'INTÉRIEUR de la bulle, centrée
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', 'bold');
      const letter = String.fromCharCode(65 + i); // A, B, C, D...

      // Utiliser le même fontHeight calculé précédemment
      const letterY = bubbleY + fontHeight / 3;
      doc.text(letter, bubbleX, letterY, { align: 'center' });
    }

  };

  // Fonction pour dessiner une partie avec son cadre
  const drawPart = (part: Part, questions: Question[], x: number, y: number, width: number, height: number) => {
    // Titre de la partie AU-DESSUS du cadre (plus grand)
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(part.titre, x + width / 2, y - 3, { align: 'center' });

    // Cadre de la partie
    doc.setLineWidth(0.3);
    doc.rect(x, y, width, height);

    // Calculer dynamiquement le nombre optimal de colonnes
    const maxQuestionsPerColumn = Math.floor(height / questionHeight);
    const optimalColumns = Math.ceil(questions.length / maxQuestionsPerColumn);
    const questionsPerColumn = Math.ceil(questions.length / optimalColumns);
    const columnWidth = width / optimalColumns;

    // Dessiner les questions dans le cadre
    questions.forEach((question, index) => {
      const column = Math.floor(index / questionsPerColumn);
      const row = index % questionsPerColumn;

      const questionX = x + column * columnWidth;
      const questionY = y + row * questionHeight;

      drawQuestion(question, questionX, questionY, columnWidth);
    });
  };

  // Dessiner les marqueurs ArUco aux 4 coins
  drawArucoMarker(doc, MARKER_IDS.TOP_LEFT, markerInset, markerInset - 2, markerSize);
  drawArucoMarker(doc, MARKER_IDS.TOP_RIGHT, pageWidth - markerInset - markerSize, markerInset - 2, markerSize);
  drawArucoMarker(doc, MARKER_IDS.BOTTOM_LEFT, markerInset, pageHeight - bottomMarkerInset - markerSize, markerSize);
  drawArucoMarker(doc, MARKER_IDS.BOTTOM_RIGHT, pageWidth - markerInset - markerSize, pageHeight - bottomMarkerInset - markerSize, markerSize);

  // Trier les parties par ordre
  const sortedParts = test.parts.sort((a, b) => a.ordre - b.ordre);

  // Calculer la largeur de chaque partie
  const titleHeight = FORM_LAYOUT.titleHeight;

  if (sortedParts.length > 0) {
    const partWidth = contentWidth / sortedParts.length;

    sortedParts.forEach((part, index) => {
      const partQuestions = questionsByPart[`part_${part.id}`] || [];
      if (partQuestions.length > 0) {
        const x = margin + index * partWidth;
        const y = markerInsetTop + titleHeight;
        const height = contentHeight - titleHeight;

        drawPart(part, partQuestions, x, y, partWidth, height);
      }
    });
  } else {
    // Si pas de parties, dessiner toutes les questions dans un seul cadre
    const allQuestions = questionsByPart['no_part'] || [];
    if (allQuestions.length > 0) {
      const y = markerInsetTop;
      const height = contentHeight;

      // Cadre pour toutes les questions
      doc.setLineWidth(0.3);
      doc.rect(margin, y, contentWidth, height);

      // Calculer dynamiquement le nombre optimal de colonnes
      const maxQuestionsPerColumn = Math.floor(height / questionHeight);
      const optimalColumns = Math.ceil(allQuestions.length / maxQuestionsPerColumn);
      const questionsPerColumn = Math.ceil(allQuestions.length / optimalColumns);
      const columnWidth = contentWidth / optimalColumns;

      allQuestions.forEach((question, index) => {
        const column = Math.floor(index / questionsPerColumn);
        const row = index % questionsPerColumn;

        const questionX = margin + column * columnWidth;
        const questionY = y + row * questionHeight;

        drawQuestion(question, questionX, questionY, columnWidth);
      });
    }
  }

  // Télécharger le PDF
  const fileName = `${test.title.replace(/[^a-z0-9]/gi, '_')}_formulaire_bulles.pdf`;
  doc.save(fileName);
};
